import { Clock3, Mail, MapPin, Phone } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { LeadForm } from "@/components/forms/LeadForm";
import { PageHero } from "@/components/shared/PageHero";
import { useSiteContent } from "@/hooks/useSiteContent";

export default function ContactPage() {
  const { siteData } = useSiteContent();

  return (
    <div className="space-y-24 pb-6" data-testid="contact-page">
      <PageHero description="Reach Monarch for roofing guidance, solar questions, quote requests, or property planning support in Los Angeles." eyebrow="Contact us" imageUrl={siteData.about_page.team_image} phone={siteData.phone} primaryHref="#contact-form-section" primaryLabel="Send message" testIdPrefix="contact-hero" title="Let’s start your quote request or answer your next project question." />

      <section className="px-4 md:px-6" data-testid="contact-details-section">
        <div className="page-section grid gap-6 lg:grid-cols-3">
          <Card className="rounded-[2rem] border-white/60 bg-white/90" data-testid="contact-phone-card">
            <CardContent className="space-y-4 p-6">
              <Phone className="h-8 w-8 text-primary" />
              <h2 className="text-2xl font-bold text-foreground" data-testid="contact-phone-title">Phone</h2>
              <a className="text-sm text-muted-foreground" data-testid="contact-phone-link" href={`tel:${siteData.phone}`}>
                {siteData.phone}
              </a>
            </CardContent>
          </Card>
          <Card className="rounded-[2rem] border-white/60 bg-white/90" data-testid="contact-email-card">
            <CardContent className="space-y-4 p-6">
              <Mail className="h-8 w-8 text-primary" />
              <h2 className="text-2xl font-bold text-foreground" data-testid="contact-email-title">Email</h2>
              <a className="text-sm text-muted-foreground" data-testid="contact-email-link" href={`mailto:${siteData.email}`}>
                {siteData.email}
              </a>
            </CardContent>
          </Card>
          <Card className="rounded-[2rem] border-white/60 bg-white/90" data-testid="contact-address-card">
            <CardContent className="space-y-4 p-6">
              <MapPin className="h-8 w-8 text-primary" />
              <h2 className="text-2xl font-bold text-foreground" data-testid="contact-address-title">Address</h2>
              <p className="text-sm text-muted-foreground" data-testid="contact-address-text">{siteData.address}</p>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="px-4 md:px-6" data-testid="contact-form-map-section" id="contact-form-section">
        <div className="page-section grid gap-8 xl:grid-cols-[0.95fr_1.05fr] xl:items-start">
          <div className="space-y-6">
            <LeadForm buttonLabel="Send message" description="Tell us what you need and our team will reach back out with the right next step." leadType="contact" pageSource="contact-form" testIdPrefix="contact-form" title="Send Monarch a message" />
            <Card className="rounded-[2rem] border-white/60 bg-slate-950 text-white" data-testid="contact-hours-card">
              <CardContent className="space-y-4 p-6">
                <Clock3 className="h-8 w-8 text-emerald-300" />
                <h2 className="text-2xl font-bold" data-testid="contact-hours-title">Office hours</h2>
                <p className="text-sm text-white/75" data-testid="contact-hours-text">
                  Monday–Friday, 8:00 AM to 6:00 PM. Call anytime to request a quote or discuss your project.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="overflow-hidden rounded-[2rem] border border-white/60 bg-white p-3 shadow-[0_18px_70px_rgba(16,24,40,0.08)]" data-testid="contact-map-wrapper">
            <iframe
              allowFullScreen
              className="min-h-[520px] w-full rounded-[1.5rem] border-0"
              data-testid="contact-map-embed"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              src="https://www.google.com/maps?q=515%20S%20Flower%20St%2C%2018th%20Floor%2C%20Los%20Angeles%2C%20CA%2090071&z=15&output=embed"
              title="Monarch Solar & Roofing Solutions map"
            />
          </div>
        </div>
      </section>
    </div>
  );
}